import type { Lesson, LessonSet, LessonBundle } from '@ezishizi/content-schema';
import type { MasteryMap } from './mastery.ts';
import type { Rng } from './rng.ts';
import { type BuildContext, buildTest, lessonEntries } from './questions.ts';
import type { Question } from './types.ts';

/** Synthetic lesson id for the merged bundle a daily challenge is built from. */
export const CHALLENGE_LESSON_ID = 'daily';

export interface ChallengeCandidate {
  character: string;
  lessonId: string;
  isWriteTarget: boolean;
}

/**
 * Weight a character for the daily challenge draw. Only mastery rows the child has actually
 * attempted say anything about strength, so untried skills are ignored; a character with no
 * attempted rows at all is treated as a perfect (weight 1) unknown rather than penalised.
 * `weakest` is the lowest score among its attempted rows (100 when there are none), and the
 * weight rises linearly from 1 (weakest = 100) to 5 (weakest = 0) — a character the child gets
 * wrong every time is five times as likely to be drawn as one it has nailed. Anything with a
 * skill due for review on top of that gets an extra ×1.5, so the challenge doubles as an SRS nudge.
 */
export function challengeWeight(mastery: MasteryMap, character: string, now: Date): number {
  const nowMs = now.getTime();
  let weakest = 100;
  let due = false;
  for (const m of mastery.values()) {
    if (m.character !== character || m.attempts <= 0) continue;
    if (m.score < weakest) weakest = m.score;
    if (m.dueAt && Date.parse(m.dueAt) <= nowMs) due = true;
  }
  const base = 1 + 4 * (1 - weakest / 100);
  return due ? base * 1.5 : base;
}

/** Weighted sampling without replacement: up to `n` distinct characters, weakest/most-due ones
 * favoured. Returns the whole pool (in a random order) when it's smaller than `n`. */
export function selectChallengeCharacters(
  candidates: readonly ChallengeCandidate[],
  mastery: MasteryMap,
  now: Date,
  n: number,
  rng: Rng,
): string[] {
  const remaining = [...new Set(candidates.map((c) => c.character))];
  const weights = new Map(remaining.map((c) => [c, challengeWeight(mastery, c, now)]));
  const out: string[] = [];
  while (remaining.length > 0 && out.length < n) {
    const total = remaining.reduce((sum, c) => sum + weights.get(c)!, 0);
    let r = rng() * total;
    let idx = remaining.length - 1;
    for (let i = 0; i < remaining.length; i++) {
      r -= weights.get(remaining[i]!)!;
      if (r <= 0) {
        idx = i;
        break;
      }
    }
    out.push(remaining[idx]!);
    remaining.splice(idx, 1);
  }
  return out;
}

/**
 * Build one synthetic lesson bundle out of several source lessons, so a mixed daily challenge
 * can be built and rendered the same way any lesson's questions are. One `LessonSet` per source
 * lesson (keeping distractor pools sensible, since questions.ts's wordPool/lessonCharSet walk
 * `bundle.lesson.sets` first), each holding that lesson's entries filtered to `chars`; a source
 * lesson with no matching entries is dropped entirely.
 */
export function mergeChallengeBundle(
  bundles: readonly LessonBundle[],
  chars: ReadonlySet<string>,
  higherChinese: boolean,
  meta: { levelId: string },
): LessonBundle {
  if (bundles.length === 0) throw new Error('mergeChallengeBundle requires at least one bundle');
  const first = bundles[0]!;
  const sets: LessonSet[] = [];
  for (const b of bundles) {
    const entries = lessonEntries(b.lesson, higherChinese).filter((e) => chars.has(e.char));
    if (entries.length === 0) continue;
    sets.push({ id: b.lesson.id, order: sets.length + 1, entries });
  }
  const lesson: Lesson = {
    id: CHALLENGE_LESSON_ID,
    // Profiles store levelId as a plain string; Lesson's schema pins it to the known level ids.
    levelId: meta.levelId as Lesson['levelId'],
    editionId: first.lesson.editionId,
    order: 1,
    semester: first.lesson.semester,
    title: null,
    sets,
  };
  return {
    schemaVersion: 1,
    contentHash: bundles.map((b) => b.contentHash).join('+'),
    lesson,
    characters: Object.assign({}, ...bundles.map((b) => b.characters)),
    words: Object.assign({}, ...bundles.map((b) => b.words)),
    // Taught by one source lesson is taught: the union, in first-seen order.
    knownWordIds: [...new Set(bundles.flatMap((b) => b.knownWordIds))],
    sentences: Object.assign({}, ...bundles.map((b) => b.sentences)),
    stories: Object.assign({}, ...bundles.map((b) => b.stories)),
    sceneSets: Object.assign({}, ...bundles.map((b) => b.sceneSets)),
    builds: Object.assign({}, ...bundles.map((b) => b.builds)),
    // The daily challenge is a character/word drill over past lessons, not a reading practice
    // session — no passage or question of one lesson's practice set makes sense merged with
    // another's, so the merged bundle simply carries none.
    passages: {},
    practiceQuestions: {},
    practices: {},
    instructions: Object.assign({}, ...bundles.map((b) => b.instructions)),
    phrases: Object.assign({}, ...bundles.map((b) => b.phrases)),
    media: Object.assign({}, ...bundles.map((b) => b.media)),
  };
}

/**
 * A daily challenge: test-style questions (4 choices, memory tracing, never the same character
 * twice in a row) over just the given characters. `buildTest` already spreads repeats out, so
 * slicing down to `count` keeps that property.
 */
export function buildDailyChallenge(
  ctx: BuildContext,
  chars: readonly string[],
  count: number,
): Question[] {
  const set = new Set(chars);
  const entries = lessonEntries(ctx.bundle.lesson, ctx.higherChinese).filter((e) =>
    set.has(e.char),
  );
  if (entries.length === 0) return [];
  const perChar = Math.max(1, Math.ceil(count / entries.length));
  const questions = buildTest(ctx, entries, perChar, 'challenge');
  return questions.slice(0, count);
}
